import { sortBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    SelectInput,
    TasksStore,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsFileEarmark } from '@react-icons/all-files/bs/BsFileEarmark';
import { Button, Form, FormGroup, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { PartsStore } from './PartsStore';

interface ISelectOption {
    value: string;
    label: string;
}

export interface IAddPartParams {
    part: Mri.Parts.Part;
    topLevelGuid: string;
    readonly modalHandle: ModalHandle;
}

@autoinject
@observer
export class AddPartModal extends React.Component<IAddPartParams> {
    public render() {
        return (
            <Modal
                title={this.title}
                onOpened={this.onOpened}
                modalHandle={this.props.modalHandle}
                footer={this.footer}
                bodyClass="p-0 vh-50"
            >
                {this.partForm && !!this.partForm!.globalErrors.length && (
                    <div className="alert-danger p-2">
                        <ul>
                            {this.partForm!.globalErrors.map((e, idx) => (
                                <li key={idx}>{e}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <Nav tabs>
                    <NavItem>
                        <NavLink
                            className={this.type == 'add' ? 'active' : ''}
                            onClick={e => this.toggelePartCreate(e, 'add')}
                        >
                            Add Part
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={this.type == 'create' ? 'active' : ''}
                            onClick={e => this.toggelePartCreate(e, 'create')}
                        >
                            Create Part
                        </NavLink>
                    </NavItem>
                </Nav>
                {!this.loading && (
                    <div className="p-3">
                        {this.applicableDefinitions.length <= 0 && (
                            <div>No template slots found or all slots filled</div>
                        )}
                    </div>
                )}
                <Form>
                    <TabContent className="p-3" activeTab={this.type}>
                        <TabPane tabId="add">
                            {this.applicableParts.length <= 0 && this.applicableDefinitions.length > 0 && (
                                <div>No parts found. Create new part</div>
                            )}

                            {/* Stange behavior here where the distinct forms, Add + Create are actually shared and need explicity rendered select dropdowns */}
                            {this.type == 'add' && (
                                <FormGroup color={this.partForm.fields.partGuid.state}>
                                    <label>
                                        <strong>Part</strong>
                                    </label>

                                    <SelectInput
                                        field={this.partForm.fields.partGuid}
                                        options={this.applicableParts}
                                        placeholder="Serial Number"
                                        required={false}
                                    />
                                </FormGroup>
                            )}
                        </TabPane>
                        <TabPane tabId="create">
                            <FormGroup color={this.partForm.fields.partDefinitionGuid.state}>
                                <label>
                                    <strong>Part Definition</strong>
                                </label>

                                <SelectInput
                                    field={this.partForm.fields.partDefinitionGuid}
                                    options={this.applicableDefinitions}
                                    placeholder="Part Number"
                                />
                            </FormGroup>
                            <FormGroup color={this.partForm!.fields.serialNumber.state}>
                                <label>Serial Number</label>

                                <Input type="text" id="name" field={this.partForm!.fields.serialNumber} />
                            </FormGroup>
                        </TabPane>
                    </TabContent>
                </Form>
            </Modal>
        );
    }

    @computed
    private get title() {
        return (
            <span className="text-header fw-bold">
                <BsFileEarmark /> Edit <span>{this.props.part.partNumber}</span>
                {this.props.part.revision && <span className="ms-1">r.{this.props.part.revision}</span>}
                <span className="ms-2">#{this.props.part.serialNumber}</span>
            </span>
        );
    }

    @computed
    private get footer() {
        return (
            <>
                <Button
                    color="primary"
                    onClick={this.submitAsync}
                    disabled={this.loading || this.applicableDefinitions.length <= 0}
                >
                    <Loader loading={this.loading} showSpinner message={`${this.titleString}ing Part...`} />
                    {!this.loading && `${this.titleString} Part`}
                </Button>
            </>
        );
    }

    @computed
    private get titleString() {
        if (this.type == 'create') return 'Create';
        if (this.type == 'add') return 'Add';
    }

    @computed
    private get applicableDefinitions(): ISelectOption[] {
        const applicableDefs = this.partsStore.applicableChildDefinitions.get(this.props.part.partGuid);

        if (applicableDefs) {
            let defs = applicableDefs.map(a => {
                return {
                    value: a.partDefinitionGuid,
                    label: a.partNumber + ' R.' + a.revision,
                };
            });

            defs = sortBy(defs, a => a.label);
            return defs;
        }

        return [];
    }

    @computed
    private get applicableParts(): ISelectOption[] {
        const appParts = this.partsStore.applicableChildParts.get(this.props.part.partGuid);

        if (appParts) {
            let parts = appParts.map(a => {
                return {
                    value: a.partGuid,
                    label: a.serialNumber,
                };
            });

            parts = sortBy(parts, a => a.label);
            return parts;
        }

        return [];
    }

    @bind
    @action
    public async onOpened() {
        this.loading = true;
        try {
            await this.partsStore.loadApplicableChildrenAsync(this.props.part.partGuid);
            await this.partsStore.loadApplicableChildDefsAsync(this.props.part.partGuid);

            if (this.applicableParts.length <= 0 && this.applicableDefinitions.length > 0) this.type = 'create';
            else this.type = 'add';
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async submitAsync() {
        if (!this.partForm) throw new Error('Form does not exist');

        this.partForm.onSubmit();
        if (this.partForm.validate()) {
            this.loading = true;
            const task = this.tasksStore.startSessionTask(`${this.titleString} Part`);

            try {
                if (this.type == 'create') {
                    await this.partsStore.createAsync(this.partForm.values);
                    task.complete();
                    this.type = 'add';
                    // Do not clear form

                    await this.partsStore.loadApplicableChildrenAsync(this.props.part.partGuid);
                } else if (this.type == 'add') {
                    await this.partsStore.addPartAsync(this.partForm.values, this.partForm.values.partGuid);
                    await this.partsStore.loadPartsFromGuid(this.props.topLevelGuid, true);

                    task.complete();
                    this.props.modalHandle.close();
                }
            } catch (e: any) {
                if (e.modelState) {
                    this.partForm.clearGlobalErrors();
                    this.partForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.loading = false;
            }
        } else {
            console.log('invalid');
            this.loading = false;
        }
    }

    @bind
    @action
    private toggelePartCreate(event: React.MouseEvent<HTMLElement>, type: 'create' | 'add') {
        event.preventDefault();

        this.type = type;
    }

    @observable private type: 'create' | 'add' = 'add';
    @observable private loading = false;
    @inject private readonly partsStore!: PartsStore;
    @inject private tasksStore!: TasksStore;
    @inject private readonly accountStore!: AccountStore;

    @observable private partForm = new FormStore<Mri.Parts.Part>({
        partNumber: '',
        revision: '',
        serialNumber: '',
        isAssembly: false,
        description: '',
        modelDescription: '',
        modelNumber: '',
        partGuid: uuidv4generate(),
        children: null,
        parentPartGuid: this.props.part.partGuid,
        partStatus: {
            userId: this.accountStore.user!.id,
            startDate: new Date(),
            partState: 1,
            comments: null,
        },
        partDefinitionGuid: null,
    });
}
